import { makeAutoObservable, observable, runInAction } from "mobx"
import { requestNestMethod } from "../../nest/client"
import type { ProviderAccountUsage, ProviderSummary } from "../../nest/generated"

export type AccountUsageLoadStatus = "idle" | "loading" | "ready" | "error"

export interface AccountUsageItem {
    providerId: string
    label: string
    usage?: ProviderAccountUsage
    error?: string
}

export interface AccountUsageState {
    status: AccountUsageLoadStatus
    items: AccountUsageItem[]
    error?: string
    loadedAt?: string
}

type AccountUsageReader = (refresh: boolean) => Promise<AccountUsageItem[]>

const ACCOUNT_USAGE_PROVIDER_ORDER = ["codex", "claude", "grok", "opencode"] as const
const ACCOUNT_USAGE_PROVIDER_SET = new Set<string>(ACCOUNT_USAGE_PROVIDER_ORDER)
function providerSortIndex(providerId: string): number {
    const index = ACCOUNT_USAGE_PROVIDER_ORDER.findIndex((id) => id === providerId)
    return index >= 0 ? index : ACCOUNT_USAGE_PROVIDER_ORDER.length
}

function providerDisplayLabel(provider: Pick<ProviderSummary, "providerId" | "label">): string {
    if (provider.label) return provider.label
    if (provider.providerId === "codex") return "Codex"
    if (provider.providerId === "claude") return "Claude"
    if (provider.providerId === "grok") return "Grok Build"
    if (provider.providerId === "opencode") return "OpenCode"
    return provider.providerId
}

async function readAccountUsageItems(refresh: boolean): Promise<AccountUsageItem[]> {
    const providerList = await requestNestMethod("provider.list")
    const providers = providerList.providers
        .filter((provider) => ACCOUNT_USAGE_PROVIDER_SET.has(provider.providerId))
        .sort((left, right) => providerSortIndex(left.providerId) - providerSortIndex(right.providerId))

    return Promise.all(
        providers.map(async (provider): Promise<AccountUsageItem> => {
            try {
                const result = await requestNestMethod(
                    "provider.accountUsage.read",
                    {
                        providerId: provider.providerId,
                        ...(refresh ? { refresh: true } : {}),
                    },
                    { timeoutMs: 15_000 }
                )
                return {
                    providerId: provider.providerId,
                    label: providerDisplayLabel(provider),
                    usage: result.usage,
                }
            } catch (error) {
                return {
                    providerId: provider.providerId,
                    label: providerDisplayLabel(provider),
                    error: error instanceof Error ? error.message : "Account usage request failed",
                }
            }
        })
    )
}

function mergeAccountUsageItems(previous: AccountUsageItem[], current: AccountUsageItem[]): AccountUsageItem[] {
    const previousByProvider = new Map(previous.map((item) => [item.providerId, item]))
    return current.map((item) => {
        if (!item.error || item.usage) return item
        const previousItem = previousByProvider.get(item.providerId)
        return previousItem?.usage ? { ...item, usage: previousItem.usage } : item
    })
}

export class AccountUsageManager {
    state: AccountUsageState = { status: "idle", items: [] }

    private readonly read: AccountUsageReader
    private refreshPromise: Promise<void> | null = null
    private generation = 0

    constructor({
        read = readAccountUsageItems,
    }: {
        read?: AccountUsageReader
    } = {}) {
        this.read = read
        makeAutoObservable<AccountUsageManager, "read" | "refreshPromise">(
            this,
            {
                state: observable.ref,
                read: false,
                refreshPromise: false,
            },
            { autoBind: true }
        )
    }

    ensureLoaded(): Promise<void> {
        if (this.state.loadedAt) return Promise.resolve()
        return this.load(false)
    }

    refresh(): Promise<void> {
        return this.load(true)
    }

    private load(refreshProvider: boolean): Promise<void> {
        if (this.refreshPromise) return this.refreshPromise

        const refreshStartedAt = Date.now()
        const generation = this.generation
        this.state = {
            status: "loading",
            items: this.state.items,
            loadedAt: this.state.loadedAt,
        }

        const refresh = this.read(refreshProvider)
            .then((items) => {
                if (this.generation !== generation) return
                runInAction(() => {
                    const loadedAt = new Date(refreshStartedAt).toISOString()
                    this.state = {
                        status: "ready",
                        items: mergeAccountUsageItems(this.state.items, items),
                        loadedAt,
                    }
                })
            })
            .catch((error: unknown) => {
                if (this.generation !== generation) return
                runInAction(() => {
                    this.state = {
                        status: "error",
                        items: this.state.items,
                        loadedAt: this.state.loadedAt,
                        error: error instanceof Error ? error.message : "Account usage request failed",
                    }
                })
            })
            .finally(() => {
                if (this.refreshPromise === refresh) this.refreshPromise = null
            })

        this.refreshPromise = refresh
        return refresh
    }

    reset(): void {
        this.generation += 1
        this.refreshPromise = null
        this.state = { status: "idle", items: [] }
    }
}
